import React, { useCallback, useState } from 'react';

import {
  checkPhoneVerification,
  continueAfterPhoneMerge,
  skipPhoneVerification,
  startPhoneVerification,
} from '../../lib/phone_verification_api';

import useCountdown from '../../hooks/useCountdown';
import AuthButton from '../auth/AuthButton';
import AuthCodeForm from '../auth/AuthCodeForm';
import { CODE_RESEND_COOLDOWN_SECONDS } from '../auth/AuthCodeResendControl';
import AuthField from '../auth/AuthField';
import { AuthBackLink, AuthFormMessage, AuthHeader, AuthHeaderBlock, AuthPageStack } from '../auth/AuthFlow';
import Flexicon from '../shared/Flexicon';

const PHONE_CODE_LENGTH = 4;
const PHONE_FIELD = {
  name: 'phone',
  label: 'Phone number',
  type: 'tel',
  autoComplete: 'tel',
  inputMode: 'tel',
  required: true,
};
const FORM_MESSAGE_ID = 'phone-verification-form-message';
const CODE_ERROR_MESSAGE_ID = 'phone-verification-code-error';

function sanitizeCode(value, codeLength) {
  return value.replace(/\D/g, '').slice(0, codeLength);
}

function messageForError(error) {
  if (!error) return null;

  switch (error) {
    case 'invalid_phone':
      return 'Enter a valid phone number.';
    case 'invalid_code':
      return 'Invalid code';
    case 'expired':
      return 'Code is invalid or expired.';
    case 'rate_limited':
      return 'Too many attempts. Please try again later.';
    case 'support_required':
      return 'This phone number is already associated with another account. Please contact support.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export default function PhoneVerificationPage({
  initialConfirmation = false,
  initialMergeConfirmation = null,
  initialPhoneDisplay = '',
  initialResendCooldown = 0,
  skippable = false,
}) {
  const [confirmation, setConfirmation] = useState(initialConfirmation);
  const mergeConfirmation = initialMergeConfirmation;
  const [phone, setPhone] = useState(initialPhoneDisplay);
  const [phoneDisplay, setPhoneDisplay] = useState(initialPhoneDisplay);
  const [code, setCode] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useCountdown(initialResendCooldown);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const invalidPhone = error === 'invalid_phone';
  const invalidCode = error === 'invalid_code' || error === 'expired';
  const isBusy = isSubmitting || isResending || isContinuing || isSkipping;

  const handlePhoneChange = useCallback(function handlePhoneChangeCallback(event) {
    setPhone(event.target.value);
    setError(null);
  }, []);

  const handleCodeChange = useCallback(function handleCodeChangeCallback(value) {
    setCode(sanitizeCode(value, PHONE_CODE_LENGTH));
    setError(null);
  }, []);

  const handlePhoneSubmit = useCallback(
    async function handlePhoneSubmitCallback(event) {
      event.preventDefault();
      if (isBusy) return;

      const submittedPhone = new FormData(event.currentTarget).get('phone')?.toString().trim() || phone.trim();
      if (!submittedPhone) {
        setError('invalid_phone');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const { response, payload, error: responseError } = await startPhoneVerification({ phone: submittedPhone });
        if (!response.ok) {
          setError(responseError || 'provider_unavailable');
          return;
        }

        setPhone(submittedPhone);
        setPhoneDisplay(payload?.phone || submittedPhone);
        setCode('');
        setCooldownSeconds(CODE_RESEND_COOLDOWN_SECONDS);
        setConfirmation(true);
      } catch {
        setError('provider_unavailable');
      } finally {
        setIsSubmitting(false);
      }
    },
    [isBusy, phone, setCooldownSeconds],
  );

  const handleCodeSubmit = useCallback(
    async function handleCodeSubmitCallback(event) {
      event.preventDefault();
      if (isBusy || code.length !== PHONE_CODE_LENGTH) return;

      setIsSubmitting(true);
      setError(null);

      try {
        const { response, payload, error: responseError } = await checkPhoneVerification({ code });
        if (!response.ok) {
          setError(responseError || 'provider_unavailable');
          return;
        }

        window.location.assign(payload?.redirect_url || '/app/start');
      } catch {
        setError('provider_unavailable');
      } finally {
        setIsSubmitting(false);
      }
    },
    [code, isBusy],
  );

  const handleMergeContinue = useCallback(
    async function handleMergeContinueCallback() {
      if (isBusy) return;

      setIsContinuing(true);
      setError(null);

      try {
        const { response, payload, error: responseError } = await continueAfterPhoneMerge();
        if (!response.ok) {
          setError(responseError || 'provider_unavailable');
          return;
        }

        window.location.assign(payload?.redirect_url || '/app/start');
      } catch {
        setError('provider_unavailable');
      } finally {
        setIsContinuing(false);
      }
    },
    [isBusy],
  );

  const handleResend = useCallback(
    async function handleResendCallback() {
      if (isBusy || cooldownSeconds > 0) return;

      setIsResending(true);
      setError(null);

      try {
        const { response, payload, error: responseError } = await startPhoneVerification();
        if (!response.ok) {
          setError(responseError || 'provider_unavailable');
          return;
        }

        setCode('');
        setPhoneDisplay(payload?.phone || phoneDisplay);
        setCooldownSeconds(CODE_RESEND_COOLDOWN_SECONDS);
      } catch {
        setError('provider_unavailable');
      } finally {
        setIsResending(false);
      }
    },
    [cooldownSeconds, isBusy, phoneDisplay, setCooldownSeconds],
  );

  const handleDifferentNumber = useCallback(
    function handleDifferentNumberCallback() {
      if (isBusy) return;

      setPhone(phoneDisplay);
      setCode('');
      setError(null);
      setConfirmation(false);
    },
    [isBusy, phoneDisplay],
  );

  const handleSkip = useCallback(
    async function handleSkipCallback() {
      if (isBusy) return;

      setIsSkipping(true);
      setError(null);

      try {
        const { response, payload, error: responseError } = await skipPhoneVerification();
        if (!response.ok) {
          setError(responseError || 'provider_unavailable');
          return;
        }

        window.location.assign(payload?.redirect_url || '/app/start');
      } catch {
        setError('provider_unavailable');
      } finally {
        setIsSkipping(false);
      }
    },
    [isBusy],
  );

  if (mergeConfirmation) {
    const primaryUser = mergeConfirmation.primary_user;

    return (
      <AuthPageStack>
        <AuthHeader title="Welcome back!" />
        <span className="inline-flex size-45">
          <Flexicon resource={primaryUser} size="size-45" />
        </span>
        <div className="flex w-full flex-col gap-4 text-center text-[13px] leading-5 text-gray-500">
          <p>
            Hey {primaryUser.full_name}, the number <strong className="font-semibold text-black">{mergeConfirmation.phone}</strong> is
            linked to <strong className="font-semibold text-black">{primaryUser.email}</strong> on Idle. We have merged{' '}
            <strong className="font-semibold text-black">{mergeConfirmation.merged_email}</strong> to the same account.
          </p>
          <p>You can adjust your primary email later in settings.</p>
        </div>
        <div className="flex w-full flex-col gap-3">
          <AuthFormMessage error={messageForError(error)} id={FORM_MESSAGE_ID} />
          <AuthButton type="button" disabled={isContinuing} onClick={handleMergeContinue}>
            {isContinuing ? 'Continuing...' : 'Continue'}
          </AuthButton>
        </div>
      </AuthPageStack>
    );
  }

  if (!confirmation) {
    return (
      <AuthPageStack>
        <AuthHeaderBlock title="Verify your number">
          We’ll text you a code to make sure it’s you. We don’t share your number with anyone, ever.
        </AuthHeaderBlock>
        <div className="flex w-full flex-col gap-6">
          <form className="flex w-full flex-col gap-5" onSubmit={handlePhoneSubmit}>
            <AuthField
              disabled={isBusy}
              errorMessage={invalidPhone ? 'Not a valid phone number' : null}
              errorMessageId={FORM_MESSAGE_ID}
              field={PHONE_FIELD}
              hasError={invalidPhone}
              onChange={handlePhoneChange}
              value={phone}
            />
            <div className="flex w-full flex-col gap-3">
              <AuthFormMessage error={invalidPhone ? null : messageForError(error)} id={FORM_MESSAGE_ID} />
              <AuthButton disabled={isBusy}>{isSubmitting ? 'Sending...' : 'Continue'}</AuthButton>
            </div>
          </form>
          {skippable ? <AuthBackLink disabled={isBusy} label={isSkipping ? 'Skipping...' : 'Skip for now'} onClick={handleSkip} /> : null}
        </div>
      </AuthPageStack>
    );
  }

  return (
    <AuthPageStack>
      <AuthHeaderBlock title="Confirm with code">
        We texted you a code to <span className="font-semibold text-black">{phoneDisplay}</span>
      </AuthHeaderBlock>
      <div className="flex w-full flex-col items-center gap-6">
        <AuthCodeForm
          code={code}
          codeErrorMessageId={CODE_ERROR_MESSAGE_ID}
          codeLength={PHONE_CODE_LENGTH}
          disabled={isBusy}
          error={messageForError(error)}
          formMessageId={FORM_MESSAGE_ID}
          hasCodeError={invalidCode}
          isResending={isResending}
          isSubmitting={isSubmitting}
          onCodeChange={handleCodeChange}
          onResend={handleResend}
          onSubmit={handleCodeSubmit}
          pendingLabel="Continuing..."
          resendCooldownSeconds={cooldownSeconds}
          showInlineCodeError={invalidCode}
          submitLabel="Continue"
        />
        <div className="flex w-full flex-col gap-3">
          <AuthBackLink disabled={isBusy} label="Use a different number" onClick={handleDifferentNumber} />
          {skippable ? <AuthBackLink disabled={isBusy} label={isSkipping ? 'Skipping...' : 'Skip for now'} onClick={handleSkip} /> : null}
        </div>
      </div>
    </AuthPageStack>
  );
}
